import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api.js";
import type { Device } from "../lib/types.js";

/** How each device state reads in the table: dot-pill class + label. */
function stateBadge(d: Device): { cls: string; label: string } {
  switch (d.state) {
    case "licensed":
      return { cls: "status-active", label: "Licensed" };
    case "trial-active":
      return { cls: "status-trial", label: `Trial · ${d.trial?.daysRemaining ?? 0}d left` };
    case "trial-expired":
      return { cls: "", label: "Trial expired" };
    case "trial-terminated":
      return { cls: "", label: "Trial ended" };
    case "blocked":
      return { cls: "status-blocked", label: "Blocked" };
  }
}

function fmtLastSeen(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
}

/** Hover text for a blocked device: reason + who blocked it and when. */
function blockedTitle(d: Device): string {
  if (!d.blocked) return "";
  const parts: string[] = [];
  parts.push(d.blocked.reason ? `Reason: ${d.blocked.reason}` : "No reason given");
  const who = d.blocked.blockedBy ? `by ${d.blocked.blockedBy}` : null;
  const when = d.blocked.blockedAt
    ? `on ${new Date(d.blocked.blockedAt).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}`
    : null;
  const meta = [who, when].filter(Boolean).join(" ");
  if (meta) parts.push(`Blocked ${meta}`);
  return parts.join(" · ");
}

export function Devices() {
  const [devices, setDevices] = useState<Device[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [groupByCustomer, setGroupByCustomer] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [customerFilter, setCustomerFilter] = useState<string>("all");
  const [overAllocOnly, setOverAllocOnly] = useState(false);

  async function load(q = "") {
    try {
      const path = q.trim() ? `/admin/devices?q=${encodeURIComponent(q.trim())}` : "/admin/devices";
      const res = await api.get<{ devices: Device[]; truncated: boolean }>(path);
      setDevices(res.devices);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load devices");
    }
  }

  // Debounced search by Device ID or device name.
  useEffect(() => {
    const t = setTimeout(() => load(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  async function act(machineId: string, body: Record<string, unknown>, confirmMsg?: string) {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusyId(machineId);
    setError(null);
    try {
      await api.post(`/admin/devices/${machineId}`, body);
      await load(search);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Action failed");
    } finally {
      setBusyId(null);
    }
  }

  function extend(d: Device) {
    const current = d.trial?.daysRemaining ?? 7;
    const input = window.prompt("Set this trial to how many days from now? (1-90)", String(current));
    if (input === null) return;
    const days = Math.floor(Number(input));
    if (!Number.isFinite(days) || days < 1 || days > 90) {
      setError("Enter a whole number of days between 1 and 90.");
      return;
    }
    act(d.machineId, { action: "extend", days });
  }

  function block(d: Device) {
    if (!window.confirm("Block this device? Its trial is ended and, if licensed, it's removed from the licence and locks on next check-in.")) return;
    const reason = window.prompt("Reason (optional):", "") ?? "";
    act(d.machineId, { action: "block", reason: reason.trim() || undefined });
  }

  async function copyId(machineId: string) {
    try {
      await navigator.clipboard.writeText(machineId);
    } catch {
      // Clipboard blocked (no HTTPS/permission) - nothing to do; the id is visible.
    }
  }

  // The customer bucket a device belongs to (for grouping). Licensed devices group
  // by their business/customer name; everything else falls under trials.
  function groupOf(d: Device): string {
    if (d.licence) return d.licence.businessName ?? "Licensed (unnamed customer)";
    return "Trials & unlicensed";
  }

  function renderRow(d: Device) {
    const badge = stateBadge(d);
    const busy = busyId === d.machineId;
    const isTrial = d.state === "trial-active" || d.state === "trial-expired" || d.state === "trial-terminated";
    return (
      <tr key={d.machineId}>
        <td>
          <span className="device-id">
            <code>{d.shortId}</code>
            <button className="copy-id" title="Copy full Device ID" onClick={() => copyId(d.machineId)}>
              ⧉
            </button>
          </span>
        </td>
        <td className="col-clip" title={d.deviceName ?? undefined}>
          {d.deviceName ? d.deviceName : <span style={{ color: "var(--muted)" }}>—</span>}
        </td>
        <td>
          <span
            className={`status-badge ${badge.cls}`}
            title={d.blocked ? blockedTitle(d) : undefined}
            style={d.blocked ? { cursor: "help" } : undefined}
          >
            {badge.label}
          </span>
          {(d.belowMinVersion || d.hardwareSiblings > 0) && (
            <div className="row-flags">
              {d.belowMinVersion && (
                <span
                  className={d.updateDeadlineUtc && new Date(d.updateDeadlineUtc) < new Date() ? "badge-warn" : "badge-muted"}
                  title={
                    d.updateDeadlineUtc
                      ? `Below the minimum app version — locks after ${new Date(d.updateDeadlineUtc).toLocaleDateString()} (builds before 1.0.2 keep working until upgraded)`
                      : "Below the minimum app version"
                  }
                >
                  Needs update
                </span>
              )}
              {d.hardwareSiblings > 0 && (
                <span
                  className="badge-warn"
                  title="Another Device ID reports the same hardware id — usually a re-imaged PC, or someone who edited the Windows MachineGuid to restart a trial. Block it if it's the latter."
                >
                  Same hardware as {d.hardwareSiblings} other{d.hardwareSiblings === 1 ? "" : "s"}
                </span>
              )}
            </div>
          )}
        </td>
        <td className="col-clip" title={d.licence?.businessName ?? undefined}>
          <div className="cell-inline">
            {d.licence ? (
              <Link className="clip" to={`/customers/${d.licence.customerId}`}>
                {d.licence.businessName ?? "View customer"}
              </Link>
            ) : (
              "—"
            )}
            {d.overAllocated && (
              <span className="overalloc-badge" title="This licence has more active devices than seats purchased">
                Over-allocated
              </span>
            )}
          </div>
        </td>
        <td className="col-nowrap">{fmtLastSeen(d.lastSeenUtc)}</td>
        <td className="num col-nowrap">{d.appVersion ?? "—"}</td>
        <td className="col-actions">
          <div className="device-actions">
            {d.state !== "blocked" && isTrial && (
              <button disabled={busy} onClick={() => extend(d)} title="Set this trial's remaining days (not additive)">
                Set days
              </button>
            )}
            {d.state !== "blocked" && d.state !== "trial-terminated" && isTrial && (
              <button
                disabled={busy}
                onClick={() =>
                  act(d.machineId, { action: "end-trial" }, "End this device's free trial now? They can still buy a licence.")
                }
              >
                End trial
              </button>
            )}
            {d.state === "blocked" ? (
              <button
                disabled={busy}
                onClick={() => act(d.machineId, { action: "unblock" }, "Unblock this device? It can activate again on its next check-in.")}
              >
                Unblock
              </button>
            ) : (
              <button className="danger" disabled={busy} onClick={() => block(d)}>
                Block
              </button>
            )}
            <button
              className="danger"
              disabled={busy}
              onClick={() =>
                act(
                  d.machineId,
                  { action: "remove" },
                  "Remove this device from the console? It's cleared from any licence (freeing the seat) and its trial record is deleted. If it checks in again it reappears as a new device. To ban it instead, use Block."
                )
              }
            >
              Remove
            </button>
          </div>
        </td>
      </tr>
    );
  }

  // Customer names present in the loaded page (for the customer filter dropdown).
  const customerNames = Array.from(
    new Set((devices ?? []).map((d) => d.licence?.businessName).filter((n): n is string => !!n))
  ).sort((a, b) => a.localeCompare(b));

  // Client-side filters applied on top of the loaded page (search narrows server-side).
  const filtered = (devices ?? []).filter((d) => {
    if (statusFilter === "needs-update") {
      if (!d.belowMinVersion) return false;
    } else if (statusFilter !== "all" && d.state !== statusFilter) return false;
    if (overAllocOnly && !d.overAllocated) return false;
    if (customerFilter === "__none__" && d.licence) return false;
    if (customerFilter !== "all" && customerFilter !== "__none__" && (d.licence?.businessName ?? null) !== customerFilter)
      return false;
    return true;
  });

  const anyFilterActive = statusFilter !== "all" || customerFilter !== "all" || overAllocOnly || !!search.trim();

  // Flat, or grouped under customer heading rows.
  function bodyRows() {
    if (!devices) return null;
    if (filtered.length === 0) {
      return (
        <tr>
          <td colSpan={7}>{anyFilterActive ? "No devices match your filters." : "No devices yet."}</td>
        </tr>
      );
    }
    if (!groupByCustomer) return filtered.map(renderRow);

    const sorted = [...filtered].sort(
      (a, b) => groupOf(a).localeCompare(groupOf(b)) || (b.lastSeenUtc ?? "").localeCompare(a.lastSeenUtc ?? "")
    );
    const out: ReactNode[] = [];
    let current: string | null = null;
    for (const d of sorted) {
      const g = groupOf(d);
      if (g !== current) {
        current = g;
        out.push(
          <tr key={`group-${g}`} className="group-heading">
            <td colSpan={7}>{g}</td>
          </tr>
        );
      }
      out.push(renderRow(d));
    }
    return out;
  }

  return (
    <div className="page-wide">
      <h1>Devices</h1>
      <p style={{ color: "var(--muted)", fontSize: 13, margin: "0 0 10px" }}>
        Every machine that's run a trial or activated a licence, keyed by its Device ID.
      </p>
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", margin: "4px 0 10px" }}>
        <input
          type="search"
          placeholder="Search by Device ID or device name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: "1 1 280px", maxWidth: 480, margin: 0 }}
          aria-label="Search devices"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label="Filter by state"
          style={{ margin: 0, fontSize: 13, padding: "7px 10px" }}
        >
          <option value="all">All states</option>
          <option value="licensed">Licensed</option>
          <option value="trial-active">Trial active</option>
          <option value="trial-expired">Trial expired</option>
          <option value="trial-terminated">Trial ended</option>
          <option value="blocked">Blocked</option>
          <option value="needs-update">Needs update</option>
        </select>
        <select
          value={customerFilter}
          onChange={(e) => setCustomerFilter(e.target.value)}
          aria-label="Filter by customer"
          style={{ margin: 0, fontSize: 13, padding: "7px 10px", maxWidth: 220 }}
        >
          <option value="all">All customers</option>
          <option value="__none__">Unlicensed / trials</option>
          {customerNames.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 16 }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 13, color: "var(--ink-soft)", whiteSpace: "nowrap", cursor: "pointer" }}>
            <input type="checkbox" checked={overAllocOnly} onChange={(e) => setOverAllocOnly(e.target.checked)} />
            Over-allocated only
          </label>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 13, color: "var(--ink-soft)", whiteSpace: "nowrap", cursor: "pointer" }}>
            <input type="checkbox" checked={groupByCustomer} onChange={(e) => setGroupByCustomer(e.target.checked)} />
            Group by customer
          </label>
        </div>
      </div>
      {error && <p className="error-text">{error}</p>}
      {!devices && !error && <p>Loading…</p>}
      {devices && (
        <p style={{ color: "var(--muted)", fontSize: 12.5, margin: "0 0 8px" }}>
          Showing {filtered.length}
          {filtered.length !== devices.length ? ` of ${devices.length}` : ""} device{filtered.length === 1 ? "" : "s"}
          {anyFilterActive ? " (filtered)" : ""}.
        </p>
      )}
      {devices && (
        <div className="table-scroll">
          <table className="data-table devices-table">
            {/* Fixed column grid (see .devices-table): every row lines up regardless of how long a
                device or customer name is - those two columns clip with a tooltip instead of
                pushing the rest of the row about. Widths sum to ~1100px + the two flexible columns. */}
            <colgroup>
              <col style={{ width: 150 }} />
              <col style={{ width: "22%" }} />
              <col style={{ width: 186 }} />
              <col />
              <col style={{ width: 150 }} />
              <col style={{ width: 86 }} />
              <col style={{ width: 316 }} />
            </colgroup>
            <thead>
              <tr>
                <th className="col-nowrap">Device ID</th>
                <th>Device name</th>
                <th>State</th>
                <th>Customer / Licence</th>
                <th className="col-nowrap">Last seen</th>
                <th className="num col-nowrap">Version</th>
                <th className="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>{bodyRows()}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
